import { useEffect, useState } from 'react';
import Bowser from 'bowser';
import { detectIncognito } from 'detectincognitojs';

export type UseBowserParserType = Partial<Bowser.Parser.ParsedResult> & {
  isDDG?: boolean;
  isDesktop: boolean;
  isIpad: boolean;
  isIphone: boolean;
  isMobile: boolean;
  browserName: string;
  isPrivateMode: boolean;
  isSafari: boolean;
  isTablet: boolean;
  isValidIOSVersion: boolean;
  loading: boolean;
  isApple: boolean;
  hasNDEFReader: boolean;
  nfcEnabled: boolean;
};

export function useBowserParser(): UseBowserParserType {
  const { DEV, VITEST } = import.meta.env;

  const [userAgent, setUserAgent] = useState<string | null>(null);
  const [result, setResult] = useState<Bowser.Parser.ParsedResult | null>(null);
  const [bowserLoading, setBowserLoading] = useState(true);
  const [isPrivate, setIsPrivate] = useState(false);
  const [nfcEnabled, setNFCEnabled] = useState(true);

  const osMajVersion = result?.os.version?.split('.')[0] || 0;

  const isDesktop = result?.platform.type === 'desktop';
  const isDDG = userAgent?.toLowerCase().includes('duckduckgo');
  const isIpad = result?.platform.model === 'iPad';
  const isIphone = result?.platform.model === 'iPhone';
  const isMobile = result?.platform.type === 'mobile';
  const isSafari = result?.browser.name === 'Safari';
  const isTablet = result?.platform.type === 'tablet';
  const browserName = result?.browser.name || '';
  const isValidIOSVersion = Number(osMajVersion) >= 16;
  const isApple = result?.os.name === 'iOS' || result?.os.name === 'macOS';
  const hasNDEFReader = 'NDEFReader' in window;

  const testingOverrides = localStorage.getItem('testingHarness');

  useEffect(() => {
    const agent = window.navigator.userAgent;
    setResult(Bowser.parse(agent));
    setUserAgent(agent);
    if (VITEST) {
      setIsPrivate(false);
      setBowserLoading(false);
    } else {
      detectIncognito().then((incognitoResult) => {
        setIsPrivate(incognitoResult.isPrivate);
        setBowserLoading(false);
      });
    }
  }, [VITEST]);

  useEffect(() => {
    if (!('NDEFReader' in window)) {
      return;
    }
    navigator.permissions
      .query({ name: 'nfc' as PermissionName })
      .then((status) => {
        setNFCEnabled(status.state !== 'denied');
        status.onchange = () => {
          setNFCEnabled(status.state !== 'denied');
        };
      })
      .catch(() => {
        // browser doesn't support querying nfc permission — leave as true
      });
  }, []);

  if (testingOverrides && DEV) {
    try {
      const parsedJson = JSON.parse(testingOverrides);
      return parsedJson;
    } catch (e) {
      console.log(e);
    }
  }

  return {
    ...result,
    isDDG,
    isDesktop,
    isIpad,
    isIphone,
    isMobile,
    browserName,
    isPrivateMode: isPrivate,
    isApple,
    hasNDEFReader,
    nfcEnabled,
    isSafari,
    isTablet,
    isValidIOSVersion,
    loading: bowserLoading,
  };
}